// src/CleanForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useSession } from './auth/SessionProvider.jsx';
import { api } from './lib/apiFetch';

const PRESIGN_PATH = '/image-upload';
const SUBMIT_PATH  = '/submitCleaning';
const UPDATE_PATH  = '/updatecleaninglist';

const CleaningForm = ({ data, onFormSubmit }) => {
  const { signedIn } = useSession();

  const [formData, setFormData] = useState({
    unitNumber: '',
    dateCleaned: '',
    cleanedBy: '',
    truckCond: '',
    cleaningNotes: '',
    cleaningType: 'Standard',
    files: [],
  });

  const [inputKey, setInputKey] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  const [duplicateEntry, setDuplicateEntry] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({}); // e.g., { unitNumber: 'Unknown unit' }

  // ---- flash helpers ------------------------------------------------------
  const timeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(timeoutRef.current), []);
  const clearFlashMessage = () => setFlashMessage('');
  const setFlash = (type, message, ms = 6000) => {
    setFlashMessage({ type, message });
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(clearFlashMessage, ms);
  };

  // ---- form handlers ------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((errs) => ({ ...errs, [name]: undefined }));
    }
  };

  const handleImageChange = (e) => {
    setFormData((s) => ({ ...s, files: Array.from(e.target.files || []) }));
  };

  const resetForm = () => {
    setFormData({
      unitNumber: '',
      dateCleaned: '',
      cleanedBy: '',
      truckCond: '',
      cleaningNotes: '',
      cleaningType: 'Standard',
      files: [],
    });
    setFieldErrors({});
    setDuplicateEntry(null);
    setInputKey(Date.now());
  };

  // ---- API helpers --------------------------------------------------------
  const getPresignedURL = async (file, newName) => {
    const body = { objectKey: newName, contentType: file.type, fileType: file.type };
    const { presignedUrl } = await api.post(PRESIGN_PATH, body, { withAuth: true });
    if (!presignedUrl) throw new Error('No presigned URL returned');
    return presignedUrl;
  };

  const uploadFileToS3 = async (file, url) => {
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Failed to upload file: ${res.status} ${txt}`);
    }
    return true;
  };

  // ---- submit -------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!signedIn) {
      setFlash('error', 'Please sign in before submitting.');
      return;
    }

    // quick client validation
    const newFieldErrors = {};
    if (!formData.unitNumber?.trim()) newFieldErrors.unitNumber = 'Unit Number is required.';
    if (!formData.dateCleaned) newFieldErrors.dateCleaned = 'Date Cleaned is required.';
    if (!formData.cleanedBy) newFieldErrors.cleanedBy = 'Cleaner is required.';
    if (Object.keys(newFieldErrors).length) {
      setFieldErrors(newFieldErrors);
      setFlash('error', 'Please fix the highlighted fields.');
      return;
    }

    setLoading(true);
    setDuplicateEntry(null);
    setFieldErrors({});

    try {
      // 1) Upload all images (if any)
      const currentDate = new Date()
        .toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
        .replace(/\//g, '');
      const truckNumber = formData.unitNumber.trim();

      const uploadedImageNames = await Promise.all(
        (formData.files || []).map(async (file, index) => {
          const newName = `nonopt.${currentDate}.${truckNumber}.${index + 1}`;
          const presignedUrl = await getPresignedURL(file, newName);
          await uploadFileToS3(file, presignedUrl);
          return newName;
        })
      );

      // 2) Submit cleaning record to type=1000
      const requestBody = {
        id: new Date().toISOString(),
        type: 1000,
        unitNumber: truckNumber,                  // trimmed
        dateCleaned: formData.dateCleaned,        // HTML date is ISO yyyy-mm-dd; backend accepts
        cleanedBy: formData.cleanedBy,
        truckCond: formData.truckCond,
        cleaningNotes: formData.cleaningNotes,
        cleaningType: formData.cleaningType,
        images: uploadedImageNames,
      };

      const submitResp = await api.post(SUBMIT_PATH, requestBody, { withAuth: true });

      // Backend structured error in 200? handle
      if (submitResp?.success === false) {
        if (submitResp.code === 'DUPLICATE') {
          setDuplicateEntry(submitResp.details?.duplicateEntry || null);
          setFlash('warning', submitResp.message || 'Duplicate entry detected.');
          return;
        }
        throw new Error(submitResp.message || 'Submission failed.');
      }

      // 3) Always update the type=900 cleaning-list item (backend now branches by cleaningType)
      try {
        await api.post(
          UPDATE_PATH,
          {
            unitNumber: truckNumber,
            dateCleaned: formData.dateCleaned,   // ISO string; Lambda formats to MM/DD/YYYY
            cleaningType: formData.cleaningType, // <-- important for Standard vs Emergency/Intake
          },
          { withAuth: true }
        );
      } catch (e2) {
        // Non-fatal: the main cleaning was saved; surface a gentle warning
        console.warn('[CleanForm] updatecleaninglist failed:', e2);
        setFlash('warning', 'Saved cleaning, but list update failed—lists may be stale briefly.');
      }

      setFlash('success', 'Form submitted successfully!');
      resetForm();
      if (typeof onFormSubmit === 'function') onFormSubmit();
    } catch (err) {
      // Map server-side structured errors to friendly UI text
      const code = err?.code;
      if (code === 'UNIT_NOT_FOUND') {
        const badUnit = err?.details?.unitNumber || formData.unitNumber;
        setFieldErrors((fe) => ({ ...fe, unitNumber: `Unknown unit: ${badUnit}` }));
        setFlash('error', `Unknown unit: ${badUnit}. Please select a valid unit number.`);
      } else if (code === 'DUPLICATE') {
        setDuplicateEntry(err?.details?.duplicateEntry || null);
        setFlash('warning', 'A cleaning for this unit/type already exists in the last 24 hours.');
      } else if (code === 'INVALID_INPUT') {
        const fields = err?.details?.fields || [];
        if (fields.length) {
          const next = { ...fieldErrors };
          fields.forEach((msg) => {
            if (/unitNumber/i.test(msg)) next.unitNumber = msg;
            if (/dateCleaned/i.test(msg)) next.dateCleaned = msg;
            if (/cleanedBy/i.test(msg)) next.cleanedBy = msg;
          });
          setFieldErrors(next);
        }
        setFlash('error', 'Please fix the highlighted fields.');
      } else if (code === 'UNKNOWN_CLEANING_TYPE') {
        const allowed = (err?.details?.allowed || []).join(', ');
        setFlash('error', `Invalid cleaning type for this unit. Allowed: ${allowed || '—'}`);
      } else if (err?.status === 401 || err?.status === 403) {
        setFlash('error', 'Your session expired or is unauthorized. Please sign in again.');
      } else if (err?.status === 500 || code === 'INTERNAL_ERROR') {
        setFlash('error', 'Server error while saving. Please try again.');
      } else {
        setFlash('error', err?.message || 'Error submitting form. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- update duplicate in-place -----------------------------------------
  const handleUpdateEntry = async () => {
    if (!signedIn) {
      setFlash('error', 'Please sign in before updating.');
      return;
    }
    setLoading(true);
    try {
      const requestBody = {
        id: duplicateEntry?.id, // reuse existing ID
        type: 1000,
        unitNumber: formData.unitNumber.trim(),
        dateCleaned: formData.dateCleaned,
        cleanedBy: formData.cleanedBy,
        truckCond: formData.truckCond,
        cleaningNotes: formData.cleaningNotes,
        cleaningType: formData.cleaningType,
        images: duplicateEntry?.images || (formData.files || []).map((f) => f.name),
        isUpdate: true,
      };

      const resp = await api.post(SUBMIT_PATH, requestBody, { withAuth: true });
      if (resp?.success === false) {
        throw new Error(resp?.message || 'Failed to update existing entry.');
      }

      // Keep lists in sync after update as well
      try {
        await api.post(
          UPDATE_PATH,
          {
            unitNumber: formData.unitNumber.trim(),
            dateCleaned: formData.dateCleaned,
            cleaningType: formData.cleaningType,
          },
          { withAuth: true }
        );
      } catch (e2) {
        console.warn('[CleanForm] updatecleaninglist (after update) failed:', e2);
      }

      setFlash('success', 'Entry updated successfully!');
      setDuplicateEntry(null);
      resetForm();
      if (typeof onFormSubmit === 'function') onFormSubmit();
    } catch (err) {
      setFlash('error', err?.message || 'Failed to update entry.');
    } finally {
      setLoading(false);
    }
  };

  // ---- styles for per-field errors ----------------------------------------
  const errorStyle = { border: '2px solid crimson', outline: 'none' };
  const hintStyle  = { color: 'crimson', fontSize: 12, marginTop: 4 };

  return (
    <div className="container formDiv right">
      {!signedIn && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: 'crimson', marginBottom: 8 }}>
            You must sign in to submit cleaning forms.
          </div>
          <button className="link-btn" onClick={() => signInWithRedirect()}>
            Sign in
          </button>
        </div>
      )}

      <form action="/submit" method="post" className="cleaning-form" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="unitNumber" className="formItem">Unit Number:</label>
          <input
            type="text"
            id="unitNumber"
            name="unitNumber"
            className="form-input"
            value={formData.unitNumber}
            onChange={handleInputChange}
            disabled={!signedIn || loading}
            style={fieldErrors.unitNumber ? errorStyle : undefined}
          />
          {fieldErrors.unitNumber && <div style={hintStyle}>{fieldErrors.unitNumber}</div>}
        </div>

        <div>
          <label htmlFor="dateCleaned" className="formItem">Date Cleaned:</label>
          <input
            type="date"
            id="dateCleaned"
            name="dateCleaned"
            className="form-input"
            value={formData.dateCleaned}
            onChange={handleInputChange}
            disabled={!signedIn || loading}
            style={fieldErrors.dateCleaned ? errorStyle : undefined}
          />
          {fieldErrors.dateCleaned && <div style={hintStyle}>{fieldErrors.dateCleaned}</div>}
        </div>

        <div>
          <label htmlFor="cleanedBy" className="formItem">Cleaned By:</label>
          <select
            name="cleanedBy"
            id="cleanedBy"
            className="form-input"
            value={formData.cleanedBy}
            onChange={handleInputChange}
            disabled={!signedIn || loading}
            style={fieldErrors.cleanedBy ? errorStyle : undefined}
          >
            <option value="" disabled hidden>Select a staff</option>
            {Array.isArray(data) &&
              data.map((staffMember) => (
                <option key={staffMember.name} value={staffMember.name}>
                  {staffMember.name}
                </option>
              ))}
          </select>
          {fieldErrors.cleanedBy && <div style={hintStyle}>{fieldErrors.cleanedBy}</div>}
        </div>

        <div>
          <label htmlFor="cleaningType" className="formItem">Cleaning Type:</label>
          <select
            id="cleaningType"
            name="cleaningType"
            className="form-input"
            value={formData.cleaningType}
            onChange={handleInputChange}
            disabled={!signedIn || loading}
          >
            <option value="Standard">Standard</option>
            <option value="Emergency">Emergency</option>
            <option value="Intake">Intake</option>
          </select>
        </div>

        <div>
          <label htmlFor="truckCond" className="formItem">Truck Condition:</label>
          <select
            id="truckCond"
            name="truckCond"
            className="form-input"
            value={formData.truckCond}
            onChange={handleInputChange}
            disabled={!signedIn || loading}
          >
            <option value="">Select condition</option>
            <option value="excellent">Excellent</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
            <option value="poor">Poor</option>
          </select>
        </div>

        <div>
          <label htmlFor="cleaningNotes" className="formItem">Cleaning Notes:</label>
          <textarea
            id="cleaningNotes"
            name="cleaningNotes"
            className="form-input"
            value={formData.cleaningNotes}
            onChange={handleInputChange}
            disabled={!signedIn || loading}
          />
        </div>

        <div>
          <label htmlFor="fileUpload" className="formItem">File Upload:</label>
        <input
            key={inputKey}
            type="file"
            id="fileUpload"
            name="fileUpload"
            className="form-input"
            accept=".jpg,.jpeg,.png"
            onChange={handleImageChange}
            multiple
            disabled={!signedIn || loading}
          />
        </div>

        <button type="submit" className="form-button" disabled={loading || !signedIn}>
          {loading ? 'Submitting…' : 'Submit'}
        </button>
      </form>

      {duplicateEntry && (
        <div className="duplicate-entry-warning" style={{ marginTop: 12 }}>
          <p>A similar entry already exists. Do you want to update it?</p>
          <button onClick={handleUpdateEntry} disabled={!signedIn || loading}>Update Entry</button>
          <button onClick={() => setDuplicateEntry(null)} disabled={loading}>Cancel</button>
        </div>
      )}

      {flashMessage && (
        <div className={`flash-message ${flashMessage.type}`} style={{ marginTop: 12 }}>
          {flashMessage.message}
        </div>
      )}
    </div>
  );
};

export default CleaningForm;
